import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Brain,
  Zap,
  Shield,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Target,
  Clock,
  RefreshCw,
  ChevronRight,
  Info,
  Star,
  AlertCircle,
  Award,
  Activity,
  Loader,
  X,
  ExternalLink,
  Calendar,
  Users,
  DollarSign,
  Gauge,
  BarChart3,
  PieChart,
  LineChart,
  Layers,
  FileText,
  Lightbulb,
  Bookmark,
  Eye,
  Download,
  Share2,
  Filter,
  Search,
  MapPin,
  Building,
  Globe,
  Cpu,
  Database,
  Network,
  Lock,
  Key,
  UserCheck,
  Settings,
  Zap as Lightning,
  Timer,
  Calendar as CalendarIcon,
  TrendingUp as Growth,
  AlertOctagon,
  CheckCircle2,
  XCircle,
  MinusCircle,
  Plus,
  Minus,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  MoreHorizontal,
  Maximize2,
  Minimize2,
  List,
  Bell
} from 'lucide-react'
import { analyzeSecurityPosture, generateQuickInsights } from '../services/aiService'

const AISummary = ({ assessmentData, onRecommendationClick }) => {
  const [aiAnalysis, setAiAnalysis] = useState(null)
  const [quickInsights, setQuickInsights] = useState(null)
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [lastAnalyzed, setLastAnalyzed] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [lastAnalyzedAssessmentId, setLastAnalyzedAssessmentId] = useState(null);
  const [hasAnalyzedCurrentAssessment, setHasAnalyzedCurrentAssessment] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [showRecommendationModal, setShowRecommendationModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    metrics: true,
    trends: false,
    compliance: false,
    benchmarks: false
  });
  const [selectedMetricView, setSelectedMetricView] = useState('overview');
  const [filterPriority, setFilterPriority] = useState('all');
  const [sortBy, setSortBy] = useState('priority');
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [aiAnalysisDisabled, setAiAnalysisDisabled] = useState(false);

  // Get current assessment ID from the latest assessment in history
  const getCurrentAssessmentId = () => {
    if (assessmentData?.assessmentHistory && assessmentData.assessmentHistory.length > 0) {
      return assessmentData.assessmentHistory[0].id;
    }
    return null;
  };

  // Auto-analyze when assessment data changes (only for new assessments)
  useEffect(() => {
    if (assessmentData && assessmentData.score !== undefined) {
      const currentAssessmentId = getCurrentAssessmentId();
      
      // Only analyze if this is a new assessment that hasn't been analyzed yet
      // and we're not in a disabled state (which indicates browser refresh/reload)
      if (currentAssessmentId && currentAssessmentId !== lastAnalyzedAssessmentId && !aiAnalysisDisabled) {
        // Clear cache for previous assessment
        localStorage.removeItem('aiAnalysisCache');
        setHasAnalyzedCurrentAssessment(false);
        
        // Re-enable AI analysis for new assessments
        setAiAnalysisDisabled(false);
        
        handleAnalyze();
      }
    }
  }, [assessmentData, lastAnalyzedAssessmentId, aiAnalysisDisabled]);

  // Auto-refresh functionality (disabled when current assessment already analyzed or AI analysis is disabled)
  useEffect(() => {
    let interval;
    if (autoRefresh && assessmentData && !hasAnalyzedCurrentAssessment && !aiAnalysisDisabled) {
      interval = setInterval(() => {
        handleAnalyze(true);
      }, 300000); // Refresh every 5 minutes
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, assessmentData, hasAnalyzedCurrentAssessment, aiAnalysisDisabled]);

  const toggleAutoRefresh = useCallback(() => {
    setAutoRefresh(prev => !prev);
  }, []);

  // Load cached analysis from localStorage on component mount
  useEffect(() => {
    try {
      const cachedAnalysis = localStorage.getItem('aiAnalysisCache');
      if (cachedAnalysis) {
        const parsed = JSON.parse(cachedAnalysis);
        
        // Only restore if we have cached analysis data
        if (parsed.aiAnalysis) {
          setAiAnalysis(parsed.aiAnalysis);
          setQuickInsights(parsed.quickInsights);
          setLastAnalyzedAssessmentId(parsed.assessmentId);
          setHasAnalyzedCurrentAssessment(true);
          setAiAnalysisDisabled(true); // Disable AI analysis since this assessment was already analyzed
          setLastAnalyzed(parsed.lastAnalyzed ? new Date(parsed.lastAnalyzed) : null);
          setLastUpdated(parsed.lastUpdated ? new Date(parsed.lastUpdated) : null);
        }
      }
    } catch (error) {
      console.error('Error loading cached analysis:', error);
      // Clear corrupted cache
      localStorage.removeItem('aiAnalysisCache');
    }
  }, []); // Empty dependency array - only run once on mount

  // Validate cached data against current assessment when assessmentData becomes available
  useEffect(() => {
    if (assessmentData && aiAnalysis) {
      const currentAssessmentId = getCurrentAssessmentId();
      if (currentAssessmentId && lastAnalyzedAssessmentId && currentAssessmentId !== lastAnalyzedAssessmentId) {
        // Clear cache if it's for a different assessment
        localStorage.removeItem('aiAnalysisCache');
        setAiAnalysis(null);
        setQuickInsights([]);
        setHasAnalyzedCurrentAssessment(false);
        setAiAnalysisDisabled(false);
        setLastAnalyzed(null);
        setLastUpdated(null);
        setLastAnalyzedAssessmentId(null);
      }
    }
  }, [assessmentData, aiAnalysis, lastAnalyzedAssessmentId]);

  const simulateProgress = (isRefresh = false) => {
    const steps = [
      { progress: 20, step: 'Analyzing security metrics...' },
      { progress: 40, step: 'Processing threat vectors...' },
      { progress: 60, step: 'Evaluating vulnerabilities...' },
      { progress: 80, step: 'Generating recommendations...' },
      { progress: 100, step: 'Finalizing analysis...' }
    ];

    let currentStepIndex = 0;
    const interval = setInterval(() => {
      if (currentStepIndex < steps.length) {
        setAnalysisProgress(steps[currentStepIndex].progress);
        setCurrentStep(steps[currentStepIndex].step);
        currentStepIndex++;
      } else {
        clearInterval(interval);
      }
    }, 800);

    return interval;
  };

  const handleAnalyze = async (isRefresh = false) => {
    if (!assessmentData) return

    // Prevent AI analysis if disabled (to avoid using API credits)
    if (aiAnalysisDisabled) {
      console.log('AI analysis is disabled. Waiting for new assessment to be conducted.');
      setError('AI analysis is disabled until a new assessment is conducted to avoid using API credits.');
      return;
    }

    const currentAssessmentId = getCurrentAssessmentId();
    
    // Prevent repeated analysis of the same assessment unless it's a manual refresh
    if (!isRefresh && hasAnalyzedCurrentAssessment && currentAssessmentId === lastAnalyzedAssessmentId) {
      console.log('Assessment already analyzed. Waiting for new assessment.');
      return;
    }

    if (isRefresh) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }
    setError(null)
    setAnalysisProgress(0)
    setCurrentStep('Initializing AI analysis...')

    // Start progress simulation
    const progressInterval = simulateProgress(isRefresh);

    try {
      // Generate both detailed analysis and quick insights
      const [analysis, insights] = await Promise.all([
        analyzeSecurityPosture(assessmentData),
        generateQuickInsights(assessmentData)
      ])

      // Clear progress simulation
      clearInterval(progressInterval);
      setAnalysisProgress(100);
      setCurrentStep('Analysis complete!');
      
      setAiAnalysis(analysis)
      setQuickInsights(insights)
      const now = new Date()
      setLastAnalyzed(now)
      setLastUpdated(now)
      
      // Mark this assessment as analyzed
      if (currentAssessmentId) {
        setLastAnalyzedAssessmentId(currentAssessmentId);
        setHasAnalyzedCurrentAssessment(true);
        
        // Disable AI analysis to prevent further API usage until new assessment
        setAiAnalysisDisabled(true);
        
        // Cache the analysis results in localStorage
        try {
          const cacheData = {
            assessmentId: currentAssessmentId,
            aiAnalysis: analysis,
            quickInsights: insights,
            lastAnalyzed: now.toISOString(),
            lastUpdated: now.toISOString()
          };
          localStorage.setItem('aiAnalysisCache', JSON.stringify(cacheData));
        } catch (error) {
          console.error('Error caching analysis:', error);
        }
      }
      
      // Reset progress after a short delay
      setTimeout(() => {
        setAnalysisProgress(0);
        setCurrentStep('');
      }, 1500);
    } catch (err) {
      console.error('Error generating AI analysis:', err)
      clearInterval(progressInterval);
      setAnalysisProgress(0);
      setCurrentStep('');
      // Use the error message from the AI service if available
      const errorMessage = err.message || 'Failed to analyze security posture. Please try again.'
      setError(errorMessage)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'red'
      case 'high': return 'orange'
      case 'medium': return 'yellow'
      case 'low': return 'green'
      default: return 'blue'
    }
  }

  const getSeverityBasedStyle = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return {
          bg: 'bg-gradient-to-br from-red-500/20 to-red-600/20',
          border: 'border-red-400/40',
          text: 'text-red-300',
          badge: 'bg-red-500/30 text-red-200 border-red-400/50',
          icon: 'text-red-400',
          glow: 'shadow-red-500/20'
        }
      case 'high':
        return {
          bg: 'bg-gradient-to-br from-orange-500/20 to-orange-600/20',
          border: 'border-orange-400/40',
          text: 'text-orange-300',
          badge: 'bg-orange-500/30 text-orange-200 border-orange-400/50',
          icon: 'text-orange-400',
          glow: 'shadow-orange-500/20'
        }
      case 'medium':
        return {
          bg: 'bg-gradient-to-br from-yellow-500/20 to-yellow-600/20',
          border: 'border-yellow-400/40',
          text: 'text-yellow-300',
          badge: 'bg-yellow-500/30 text-yellow-200 border-yellow-400/50',
          icon: 'text-yellow-400',
          glow: 'shadow-yellow-500/20'
        }
      case 'low':
        return {
          bg: 'bg-gradient-to-br from-green-500/20 to-green-600/20',
          border: 'border-green-400/40',
          text: 'text-green-300',
          badge: 'bg-green-500/30 text-green-200 border-green-400/50',
          icon: 'text-green-400',
          glow: 'shadow-green-500/20'
        }
      default:
        return {
          bg: 'bg-gradient-to-br from-blue-500/20 to-blue-600/20',
          border: 'border-blue-400/40',
          text: 'text-blue-300',
          badge: 'bg-blue-500/30 text-blue-200 border-blue-400/50',
          icon: 'text-blue-400',
          glow: 'shadow-blue-500/20'
        }
    }
  }

  // Enhanced helper functions for dynamic features
  const calculateSecurityMetrics = () => {
    if (!assessmentData) return null;
    
    const categories = ['accessControl', 'dataProtection', 'networkSecurity', 'incidentResponse', 'training', 'compliance'];
    const metrics = {};
    
    categories.forEach(category => {
      const score = assessmentData[category] || 0;
      metrics[category] = {
        score,
        trend: Math.random() > 0.5 ? 'up' : 'down', // Simulated trend
        change: Math.floor(Math.random() * 10) + 1,
        benchmark: Math.floor(Math.random() * 20) + 70 // Industry benchmark
      };
    });
    
    return metrics;
  };

  const getComplianceMapping = (category) => {
    const mappings = {
      'Access Control': ['NIST CSF', 'ISO 27001', 'SOC 2'],
      'Data Protection': ['GDPR', 'CCPA', 'HIPAA'],
      'Network Security': ['NIST CSF', 'ISO 27001'],
      'Incident Response': ['NIST CSF', 'ISO 27001', 'SOC 2'],
      'Training': ['ISO 27001', 'SOC 2'],
      'Compliance': ['All Frameworks']
    };
    return mappings[category] || [];
  };

  const getRiskLevel = (score) => {
    if (score >= 90) return { level: 'Low', color: 'success', icon: CheckCircle2 };
    if (score >= 70) return { level: 'Medium', color: 'warning', icon: MinusCircle };
    if (score >= 50) return { level: 'High', color: 'security', icon: AlertTriangle };
    return { level: 'Critical', color: 'security', icon: AlertOctagon };
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const filterRecommendations = (recommendations) => {
    if (!recommendations) return [];
    
    let filtered = recommendations;
    
    if (filterPriority !== 'all') {
      filtered = filtered.filter(rec => rec.priority?.toLowerCase() === filterPriority);
    }
    
    // Sort recommendations
    filtered.sort((a, b) => {
      if (sortBy === 'priority') {
        const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
        return (priorityOrder[b.priority?.toLowerCase()] || 0) - (priorityOrder[a.priority?.toLowerCase()] || 0);
      }
      if (sortBy === 'category') {
        return a.category?.localeCompare(b.category) || 0;
      }
      return 0;
    });
    
    return filtered;
  };

  const getRiskIcon = (riskLevel) => {
    switch (riskLevel) {
      case 'low': return CheckCircle
      case 'medium': return AlertTriangle
      case 'high': return AlertCircle
      default: return Shield
    }
  }

  const handleRecommendationClick = (recommendation) => {
    setSelectedRecommendation(recommendation);
    setShowRecommendationModal(true);
    // Also call the parent's handler if provided
    onRecommendationClick?.(recommendation);
  }

  const closeRecommendationModal = () => {
    setShowRecommendationModal(false);
    setSelectedRecommendation(null);
  }

  if (!assessmentData) {
    return (
      <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-2xl hover:shadow-cyber-500/10 transition-all duration-300">
        <div className="text-center py-8">
          <Brain className="w-12 h-12 text-cyber-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">AI Security Analysis</h3>
          <p className="text-cyber-200/70 mb-3">Complete a security assessment to get AI-powered insights and recommendations.</p>
          <div className="bg-cyber-500/10 border border-cyber-400/30 rounded-lg p-4 text-sm text-cyber-200">
            <div className="flex items-center space-x-2 mb-2">
              <Info className="w-4 h-4 text-cyber-400" />
              <span className="font-medium text-white">Smart Analysis</span>
            </div>
            <p>AI will analyze your security posture once after each assessment completion, then wait for your next assessment to provide fresh insights.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-2xl hover:shadow-cyber-500/10 transition-all duration-300">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-cyber-500/20 rounded-lg flex items-center justify-center border border-cyber-400/30">
              <Brain className="w-5 h-5 text-cyber-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                <span>AI Security Analysis</span>
              </h2>
              <div className="flex items-center space-x-2">
                <p className="text-sm text-cyber-200/80">
                  {lastAnalyzed ? `Last analyzed ${lastAnalyzed.toLocaleTimeString()}` : 'AI-powered security insights'}
                </p>
                {aiAnalysisDisabled && (
                  <div className="flex items-center space-x-1 text-xs text-orange-300 bg-orange-500/20 px-2 py-1 rounded-full border border-orange-400/30">
                    <Lock className="w-3 h-3" />
                    <span>API Usage Paused</span>
                  </div>
                )}
                {hasAnalyzedCurrentAssessment && !aiAnalysisDisabled && (
                  <div className="flex items-center space-x-1 text-xs text-cyber-300 bg-cyber-500/20 px-2 py-1 rounded-full border border-cyber-400/30">
                    <CheckCircle className="w-3 h-3" />
                    <span>Analysis Complete</span>
                  </div>
                )}
                {autoRefresh && !hasAnalyzedCurrentAssessment && !aiAnalysisDisabled && (
                  <div className="flex items-center space-x-1 text-xs text-success-300 bg-success-500/20 px-2 py-1 rounded-full border border-success-400/30">
                    <div className="w-2 h-2 bg-success-400 rounded-full animate-pulse"></div>
                    <span>Live</span>
                  </div>
                )}
                {lastUpdated && (
                  <span className="text-xs text-cyber-200/60">
                    Updated {lastUpdated.toLocaleTimeString()}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleAutoRefresh}
              disabled={hasAnalyzedCurrentAssessment || aiAnalysisDisabled}
              className={`px-3 py-1 text-xs rounded-full transition-colors border ${
                hasAnalyzedCurrentAssessment || aiAnalysisDisabled
                  ? 'bg-white/5 text-cyber-200/50 border-white/10 cursor-not-allowed'
                  : autoRefresh 
                    ? 'bg-success-500/20 text-success-300 border-success-400/30 hover:bg-success-500/30' 
                    : 'bg-white/10 text-cyber-200 border-white/20 hover:bg-white/20'
              }`}
              title={aiAnalysisDisabled ? 'Auto-refresh disabled - AI analysis paused to save API credits' : hasAnalyzedCurrentAssessment ? 'Auto-refresh disabled until new assessment' : ''}
            >
              <div className="flex items-center space-x-1">
                <Zap className="w-3 h-3" />
                <span>{aiAnalysisDisabled ? 'Paused' : hasAnalyzedCurrentAssessment ? 'Waiting' : autoRefresh ? 'Auto ON' : 'Auto OFF'}</span>
              </div>
            </button>
            <button
              onClick={() => handleAnalyze(true)}
              disabled={loading || refreshing || aiAnalysisDisabled}
              className={`bg-gradient-to-r from-cyber-500 to-cyber-600 hover:from-cyber-600 hover:to-cyber-700 text-white px-4 py-2 rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-cyber-500/25 flex items-center space-x-2 ${aiAnalysisDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={aiAnalysisDisabled ? 'Analysis disabled to save API credits - conduct new assessment to re-enable' : hasAnalyzedCurrentAssessment ? 'Force re-analysis of current assessment' : 'Refresh analysis'}
            >
              <RefreshCw className={`w-4 h-4 ${(loading || refreshing) ? 'animate-spin' : ''}`} />
              <span>{aiAnalysisDisabled ? 'Disabled' : refreshing ? 'Refreshing...' : loading ? 'Analyzing...' : hasAnalyzedCurrentAssessment ? 'Re-analyze' : 'Refresh Analysis'}</span>
            </button>
          </div>
        </div>

        {/* Quick Insights */}
        {quickInsights && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-white/5 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-300">
              <div className="text-2xl font-bold text-cyber-400">{quickInsights.overallHealth}</div>
              <div className="text-sm text-cyber-200/80">Overall Health</div>
            </div>
            <div className="text-center p-4 bg-white/5 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-300">
              <div className="text-2xl font-bold text-warning-400">{quickInsights.riskTrend}</div>
              <div className="text-sm text-cyber-200/80">Risk Trend</div>
            </div>
            <div className="text-center p-4 bg-white/5 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-300">
              <div className="text-sm font-medium text-success-400">{quickInsights.focusArea}</div>
              <div className="text-sm text-cyber-200/80">Focus Area</div>
            </div>
            <div className="text-center p-4 bg-white/5 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-300">
              <div className="text-sm font-medium text-cyber-300">{quickInsights.nextAssessment}</div>
              <div className="text-sm text-cyber-200/80">Next Review</div>
            </div>
          </div>
        )}

        {/* Loading State */}
         {loading && (
           <motion.div 
             className="text-center py-8"
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.5 }}
           >
             <div className="space-y-6">
               <div className="relative">
                 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-400 mx-auto"></div>
                 <div className="absolute inset-0 rounded-full h-12 w-12 border-t-2 border-cyber-300/50 mx-auto animate-pulse"></div>
               </div>
               
               <div className="space-y-3">
                 <p className="text-white font-medium">AI Security Analysis in Progress</p>
                 
                 {/* Dynamic Progress Bar */}
                 <div className="w-full max-w-md mx-auto">
                   <div className="flex justify-between text-xs text-cyber-200/80 mb-1">
                     <span>{currentStep}</span>
                     <span>{analysisProgress}%</span>
                   </div>
                   <div className="w-full bg-white/20 rounded-full h-2">
                     <motion.div 
                       className="bg-gradient-to-r from-cyber-500 to-cyber-600 h-2 rounded-full"
                       initial={{ width: 0 }}
                       animate={{ width: `${analysisProgress}%` }}
                       transition={{ duration: 0.5, ease: "easeInOut" }}
                     />
                   </div>
                 </div>
                 
                 <div className="inline-flex items-center space-x-2 text-cyber-200">
                   <Brain className="w-4 h-4 animate-pulse" />
                   <span>Processing security data...</span>
                 </div>
               </div>
               
               <div className="flex justify-center space-x-1">
                 <div className="w-2 h-2 bg-cyber-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                 <div className="w-2 h-2 bg-cyber-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                 <div className="w-2 h-2 bg-cyber-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
               </div>
             </div>
           </motion.div>
         )}
         
         {/* Refreshing Indicator */}
         {refreshing && !loading && (
           <motion.div 
             className="bg-cyber-500/10 border border-cyber-400/30 rounded-xl p-3 mb-4"
             initial={{ opacity: 0, scale: 0.95 }}
             animate={{ opacity: 1, scale: 1 }}
             transition={{ duration: 0.3 }}
           >
             <div className="flex items-center justify-center space-x-2 text-cyber-300">
               <RefreshCw className="w-4 h-4 animate-spin" />
               <span className="text-sm font-medium">Refreshing AI analysis...</span>
               {analysisProgress > 0 && (
                 <span className="text-xs">({analysisProgress}%)</span>
               )}
             </div>
             {currentStep && (
               <p className="text-xs text-cyber-200/80 text-center mt-1">{currentStep}</p>
             )}
           </motion.div>
         )}

        {/* Error State */}
        {error && (
          <motion.div 
            className="bg-red-500/10 border border-red-400/30 rounded-xl p-4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center py-4">
              <motion.div
                initial={{ rotate: 0 }}
                animate={{ rotate: [0, -10, 10, -10, 0] }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
              </motion.div>
              <h4 className="text-sm font-semibold text-white mb-2">AI Analysis Error</h4>
              <p className="text-red-300 mb-4 text-sm">{error}</p>
              <div className="space-y-2">
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  {!aiAnalysisDisabled && (
                    <button
                      onClick={() => handleAnalyze()}
                      className="bg-gradient-to-r from-cyber-500 to-cyber-600 hover:from-cyber-600 hover:to-cyber-700 text-white px-4 py-2 rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-cyber-500/25 flex items-center space-x-2 mx-auto text-sm"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span>Retry Analysis</span>
                    </button>
                  )}
                  {error.includes('API key') && (
                    <button
                      onClick={() => window.open('https://platform.openai.com/api-keys', '_blank')}
                      className="bg-blue-500/20 text-blue-300 border border-blue-400/30 px-4 py-2 rounded-xl hover:bg-blue-500/30 transition-colors text-sm"
                    >
                      Check API Key
                    </button>
                  )}
                </div>
                <p className="text-xs text-red-300/70">Check your internet connection and API key</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Waiting for New Assessment Message */}
      {hasAnalyzedCurrentAssessment && aiAnalysis && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-2xl hover:shadow-cyber-500/10 transition-all duration-300"
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-cyber-500/20 rounded-full flex items-center justify-center border border-cyber-400/30">
              {aiAnalysisDisabled ? <Lock className="w-5 h-5 text-cyber-400" /> : <CheckCircle className="w-5 h-5 text-cyber-400" />}
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-white">
                {aiAnalysisDisabled ? 'AI Analysis Paused' : 'Analysis Complete'}
              </h3>
              <p className="text-sm text-cyber-200/80">
                {aiAnalysisDisabled 
                  ? 'AI analysis has been disabled to conserve API credits. Complete a new assessment to re-enable AI analysis and get updated insights.'
                  : 'AI has analyzed your current assessment. Complete a new assessment to get updated insights and recommendations.'
                }
              </p>
            </div>
            <div className="text-xs text-cyber-300/70">
              {aiAnalysisDisabled ? (
                <>
                  <Lock className="w-4 h-4 inline mr-1" />
                  API Credits Saved
                </>
              ) : (
                <>
                  <Clock className="w-4 h-4 inline mr-1" />
                  Waiting for new data
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* AI Analysis Results */}
      {aiAnalysis && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* Enhanced Executive Summary with Metrics Dashboard */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-2xl hover:shadow-cyber-500/10 transition-all duration-300">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <Activity className="w-5 h-5 text-cyber-400" />
                  <h3 className="text-lg font-semibold text-white">Executive Summary</h3>
                  {aiAnalysis.confidence && (
                    <div className="flex items-center space-x-1 text-sm text-cyber-300/80">
                      <Star className="w-4 h-4" />
                      <span>{Math.round(aiAnalysis.confidence * 100)}% confidence</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowExportOptions(!showExportOptions)}
                    className="p-2 text-cyber-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowDetailedBreakdown(!showDetailedBreakdown)}
                    className="p-2 text-cyber-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {showDetailedBreakdown ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <p className="text-cyber-200/90 leading-relaxed mb-6">{aiAnalysis.summary}</p>
              
              {/* Key Metrics Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 p-4 rounded-xl border border-green-400/30">
                  <div className="flex items-center justify-between mb-2">
                    <Shield className="w-5 h-5 text-green-400" />
                    <ArrowUp className="w-4 h-4 text-green-400" />
                  </div>
                  <div className="text-2xl font-bold text-white">{assessmentData?.score || 0}%</div>
                  <div className="text-sm text-green-300">Overall Security</div>
                </div>
                
                <div className="bg-gradient-to-br from-cyber-500/20 to-cyber-600/20 p-4 rounded-xl border border-cyber-400/30">
                  <div className="flex items-center justify-between mb-2">
                    <Target className="w-5 h-5 text-cyber-400" />
                    <span className="text-xs bg-cyber-500/30 text-cyber-300 px-2 py-1 rounded-full border border-cyber-400/30">
                      {aiAnalysis.priorityRecommendations?.length || 0}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-white">Active</div>
                  <div className="text-sm text-cyber-300">Recommendations</div>
                </div>
                
                <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 p-4 rounded-xl border border-yellow-400/30">
                  <div className="flex items-center justify-between mb-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    <Timer className="w-4 h-4 text-yellow-400" />
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {aiAnalysis.priorityRecommendations?.filter(r => r.priority === 'Critical' || r.priority === 'High').length || 0}
                  </div>
                  <div className="text-sm text-yellow-300">High Priority</div>
                </div>
                
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 p-4 rounded-xl border border-blue-400/30">
                  <div className="flex items-center justify-between mb-2">
                    <Award className="w-5 h-5 text-blue-400" />
                    <CheckCircle2 className="w-4 h-4 text-blue-400" />
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {Math.round((aiAnalysis.confidence || 0.85) * 100)}%
                  </div>
                  <div className="text-sm text-blue-300">Confidence</div>
                </div>
              </div>
              
              {/* Detailed Breakdown Toggle */}
              {showDetailedBreakdown && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="border-t border-white/20 pt-6"
                >
                  <h4 className="text-md font-semibold text-white mb-4 flex items-center space-x-2">
                    <BarChart3 className="w-4 h-4" />
                    <span>Security Metrics Breakdown</span>
                  </h4>
                  
                  {calculateSecurityMetrics() && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(calculateSecurityMetrics()).map(([category, data]) => {
                        const riskInfo = getRiskLevel(data.score);
                        const IconComponent = riskInfo.icon;
                        
                        return (
                          <div key={category} className="bg-white/5 border border-white/20 rounded-xl p-4 hover:bg-white/10 transition-all duration-300">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-2">
                                <IconComponent className={`w-4 h-4 text-${riskInfo.color}-400`} />
                                <span className="text-sm font-medium text-white capitalize">
                                  {category.replace(/([A-Z])/g, ' $1').trim()}
                                </span>
                              </div>
                              <div className="flex items-center space-x-1">
                                {data.trend === 'up' ? (
                                  <ArrowUp className="w-3 h-3 text-green-400" />
                                ) : (
                                  <ArrowDown className="w-3 h-3 text-red-400" />
                                )}
                                <span className="text-xs text-cyber-300">+{data.change}%</span>
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="text-lg font-bold text-white">{data.score}%</span>
                                <span className={`text-xs px-2 py-1 rounded-full bg-${riskInfo.color}-500/20 text-${riskInfo.color}-300 border border-${riskInfo.color}-400/30`}>
                                  {riskInfo.level} Risk
                                </span>
                              </div>
                              
                              <div className="w-full bg-white/10 rounded-full h-2">
                                <div 
                                  className={`h-2 rounded-full bg-${riskInfo.color}-500`}
                                  style={{ width: `${data.score}%` }}
                                />
                              </div>
                              
                              <div className="flex justify-between text-xs text-cyber-300/80">
                                <span>Industry Avg: {data.benchmark}%</span>
                                <span className={data.score >= data.benchmark ? 'text-green-400' : 'text-red-400'}>
                                  {data.score >= data.benchmark ? '+' : ''}{data.score - data.benchmark}%
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              )}
            </div>

            {/* Strengths and Weaknesses */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Strengths */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-2xl hover:shadow-cyber-500/10 transition-all duration-300">
                <div className="flex items-center space-x-2 mb-4">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <h3 className="text-lg font-semibold text-white">Security Strengths</h3>
                </div>
                <ul className="space-y-2">
                  {aiAnalysis.strengths?.map((strength, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                      <span className="text-cyber-200/90">{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Weaknesses */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-2xl hover:shadow-cyber-500/10 transition-all duration-300">
                <div className="flex items-center space-x-2 mb-4">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <h3 className="text-lg font-semibold text-white">Areas for Improvement</h3>
                </div>
                <ul className="space-y-2">
                  {aiAnalysis.weaknesses?.map((weakness, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                      <span className="text-cyber-200/90">{weakness}</span>
                    </li>
                  ))}
                </ul>
                {(!aiAnalysis.weaknesses || aiAnalysis.weaknesses.length === 0) && (
                  <div className="text-center py-4">
                    <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                    <p className="text-sm text-cyber-200/80">No significant weaknesses identified</p>
                  </div>
                )}
              </div>
            </div>

            {/* Enhanced Priority Recommendations */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-2xl hover:shadow-cyber-500/10 transition-all duration-300">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <Target className="w-5 h-5 text-cyber-400" />
                  <h3 className="text-lg font-semibold text-white">AI Recommendations</h3>
                  <span className="bg-cyber-500/20 text-cyber-300 px-2 py-1 rounded-full text-xs font-medium border border-cyber-400/30">
                    {filterRecommendations(aiAnalysis.priorityRecommendations || []).length} Active
                  </span>
                </div>
                
                <div className="flex items-center space-x-2">
                  {/* Filter by Category */}
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="text-sm bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-cyber-500 focus:border-cyber-400"
                  >
                    <option value="all">All Categories</option>
                    <option value="Access Control">Access Control</option>
                    <option value="Data Protection">Data Protection</option>
                    <option value="Network Security">Network Security</option>
                    <option value="Training">Training</option>
                    <option value="Compliance">Compliance</option>
                  </select>
                  
                  {/* Sort Options */}
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="text-sm bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-cyber-500 focus:border-cyber-400"
                  >
                    <option value="priority">Sort by Priority</option>
                    <option value="category">Sort by Category</option>
                    <option value="impact">Sort by Impact</option>
                    <option value="effort">Sort by Effort</option>
                  </select>
                </div>
              </div>
              
              <div className="space-y-4">
                {filterRecommendations(aiAnalysis.priorityRecommendations || []).map((rec, index) => {
                  const severityStyle = getSeverityBasedStyle(rec.priority)
                  const priorityConfig = {
                    'critical': { icon: AlertTriangle },
                    'high': { icon: AlertCircle },
                    'medium': { icon: Info },
                    'low': { icon: CheckCircle2 }
                  };
                  
                  const config = priorityConfig[rec.priority?.toLowerCase()] || priorityConfig['medium'];
                  const PriorityIcon = config.icon;
                  
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`${severityStyle.bg} ${severityStyle.border} ${severityStyle.glow} border rounded-xl p-4 hover:bg-white/5 transition-all duration-300 cursor-pointer group shadow-lg`}
                      onClick={() => handleRecommendationClick(rec)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <PriorityIcon className={`w-5 h-5 ${severityStyle.icon}`} />
                          <h4 className="font-semibold text-white group-hover:text-cyber-300 transition-colors">{rec.title}</h4>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium border ${severityStyle.badge}`}>
                            {rec.priority?.toUpperCase()}
                          </span>
                          <span className="px-2 py-1 text-xs bg-white/10 text-cyber-300 rounded-full border border-white/20">
                            {rec.category}
                          </span>
                          <ChevronRight className="w-4 h-4 text-cyber-400 group-hover:text-cyber-300 transition-colors" />
                        </div>
                      </div>
                      
                      <p className="text-cyber-200/80 mb-4 line-clamp-2">{rec.description}</p>
                      
                      <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                        <div className="flex items-center space-x-2">
                          <TrendingUp className="w-4 h-4 text-green-400" />
                          <div>
                            <span className="text-cyber-300/80">Impact:</span>
                            <div className={`font-medium ${
                              rec.impact === 'High' ? 'text-green-400' :
                              rec.impact === 'Medium' ? 'text-yellow-400' : 'text-cyber-300'
                            }`}>{rec.impact}</div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-cyber-400" />
                          <div>
                            <span className="text-cyber-300/80">Effort:</span>
                            <div className={`font-medium ${
                              rec.effort === 'Low' ? 'text-green-400' :
                              rec.effort === 'Medium' ? 'text-yellow-400' : 'text-red-400'
                            }`}>{rec.effort}</div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4 text-cyber-400" />
                          <div>
                            <span className="text-cyber-300/80">Compliance:</span>
                            <div className="font-medium text-white">
                              {getComplianceMapping(rec.category)?.slice(0, 2).join(', ') || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Implementation Progress */}
                      <div className="border-t border-white/20 pt-3">
                        <div className="flex items-center justify-between text-xs text-cyber-300/80 mb-2">
                          <span>Implementation Progress</span>
                          <span>0%</span>
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-2">
                          <div className="bg-cyber-500 h-2 rounded-full transition-all duration-300" style={{ width: '0%' }} />
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
              
              {filterRecommendations(aiAnalysis.priorityRecommendations || []).length === 0 && (
                <div className="text-center py-8">
                  <Search className="w-12 h-12 text-gov-300 mx-auto mb-3" />
                  <p className="text-gov-500">No recommendations match your current filters.</p>
                  <button
                    onClick={() => {
                      setSelectedCategory('all');
                      setSortBy('priority');
                    }}
                    className="mt-2 text-cyber-600 hover:text-cyber-700 text-sm font-medium"
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </div>

            {/* Enhanced Risk Assessment */}
            {false && aiAnalysis.riskAssessment && (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-5 h-5 text-cyber-400" />
                    <h3 className="text-lg font-semibold text-white">Risk Assessment</h3>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => toggleSection('riskDetails')}
                      className="p-2 text-cyber-300/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    >
                      {expandedSections.riskDetails ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                {/* Risk Level Indicator */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-cyber-200/90">Overall Risk Level</span>
                    <div className="flex items-center space-x-2">
                      {(() => {
                        const overallScore = assessmentData?.score || 0;
                        const riskInfo = getRiskLevel(overallScore);
                        const IconComponent = riskInfo.icon;
                        return (
                          <>
                            <IconComponent className={`w-4 h-4 ${
                              riskInfo.color === 'success' ? 'text-green-400' :
                              riskInfo.color === 'warning' ? 'text-yellow-400' :
                              riskInfo.color === 'security' ? 'text-red-400' : 'text-cyber-400'
                            }`} />
                            <span className={`text-sm font-medium ${
                              riskInfo.color === 'success' ? 'text-green-400' :
                              riskInfo.color === 'warning' ? 'text-yellow-400' :
                              riskInfo.color === 'security' ? 'text-red-400' : 'text-cyber-300'
                            }`}>
                              {riskInfo.level} Risk
                            </span>
                          </>
                        );
                      })()} 
                    </div>
                  </div>
                  
                  <div className="relative">
                    <div className="w-full bg-white/10 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full transition-all duration-500 ${
                          (assessmentData?.score || 0) >= 80 ? 'bg-green-500' :
                          (assessmentData?.score || 0) >= 60 ? 'bg-yellow-500' :
                          (assessmentData?.score || 0) >= 40 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${assessmentData?.score || 0}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-cyber-300/70 mt-1">
                      <span>High Risk</span>
                      <span>Medium Risk</span>
                      <span>Low Risk</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-medium text-white mb-3 flex items-center space-x-2">
                      <BarChart3 className="w-4 h-4 text-cyber-400" />
                      <span>Current Risk Analysis</span>
                    </h4>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                      <p className="text-cyber-200/90 leading-relaxed">{aiAnalysis.riskAssessment.currentRisk}</p>
                    </div>
                  </div>
                  
                  {aiAnalysis.riskAssessment.potentialThreats?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-white mb-3 flex items-center space-x-2">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        <span>Potential Threats</span>
                        <span className="bg-red-500/20 text-red-400 px-2 py-1 rounded-full text-xs font-medium">
                          {aiAnalysis.riskAssessment.potentialThreats.length}
                        </span>
                      </h4>
                      <div className="grid gap-3">
                        {aiAnalysis.riskAssessment.potentialThreats.map((threat, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="flex items-start space-x-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg hover:bg-red-500/15 transition-all"
                          >
                            <AlertOctagon className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <span className="text-white font-medium">{threat}</span>
                              <div className="flex items-center space-x-4 mt-2 text-xs text-cyber-300/80">
                                <div className="flex items-center space-x-1">
                                  <Timer className="w-3 h-3" />
                                  <span>Immediate attention required</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <TrendingUp className="w-3 h-3" />
                                  <span>High impact potential</span>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {aiAnalysis.riskAssessment.mitigationSteps?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-white mb-3 flex items-center space-x-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                        <span>Immediate Mitigation Steps</span>
                        <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded-full text-xs font-medium">
                          {aiAnalysis.riskAssessment.mitigationSteps.length} Actions
                        </span>
                      </h4>
                      <div className="grid gap-3">
                        {aiAnalysis.riskAssessment.mitigationSteps.map((step, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="flex items-start space-x-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg hover:bg-green-500/15 transition-all"
                          >
                            <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-xs font-bold text-green-400">{index + 1}</span>
                            </div>
                            <div className="flex-1">
                              <span className="text-white font-medium">{step}</span>
                              <div className="flex items-center space-x-4 mt-2 text-xs text-cyber-300/80">
                                <div className="flex items-center space-x-1">
                                  <Clock className="w-3 h-3" />
                                  <span>Quick implementation</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <Award className="w-3 h-3" />
                                  <span>High effectiveness</span>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Detailed Risk Breakdown */}
                  {expandedSections.riskDetails && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="border-t border-white/20 pt-6"
                    >
                      <h4 className="font-medium text-white mb-4 flex items-center space-x-2">
                        <PieChart className="w-4 h-4 text-cyber-400" />
                        <span>Risk Category Breakdown</span>
                      </h4>
                      
                      {calculateSecurityMetrics() && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(calculateSecurityMetrics()).map(([category, data]) => {
                            const riskInfo = getRiskLevel(data.score);
                            const IconComponent = riskInfo.icon;
                            
                            return (
                              <div key={category} className="bg-white/5 border border-white/10 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center space-x-2">
                                    <IconComponent className={`w-4 h-4 ${
                                      riskInfo.color === 'success' ? 'text-green-400' :
                                      riskInfo.color === 'warning' ? 'text-yellow-400' :
                                      riskInfo.color === 'security' ? 'text-red-400' : 'text-cyber-400'
                                    }`} />
                                    <span className="text-sm font-medium text-cyber-200/90 capitalize">
                                      {category.replace(/([A-Z])/g, ' $1').trim()}
                                    </span>
                                  </div>
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    riskInfo.color === 'success' ? 'bg-green-500/20 text-green-400' :
                                    riskInfo.color === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                                    riskInfo.color === 'security' ? 'bg-red-500/20 text-red-400' : 'bg-cyber-500/20 text-cyber-400'
                                  }`}>
                                    {riskInfo.level}
                                  </span>
                                </div>
                                
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-cyber-300/80">Risk Score</span>
                                    <span className="text-lg font-bold text-white">{100 - data.score}%</span>
                                  </div>
                                  
                                  <div className="w-full bg-white/10 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full ${
                                        riskInfo.color === 'success' ? 'bg-green-500' :
                                        riskInfo.color === 'warning' ? 'bg-yellow-500' :
                                        riskInfo.color === 'security' ? 'bg-red-500' : 'bg-cyber-500'
                                      }`}
                                      style={{ width: `${100 - data.score}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              </div>
            )}

            {/* Next Steps */}
            {aiAnalysis.nextSteps?.length > 0 && (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Clock className="w-5 h-5 text-cyber-400" />
                  <h3 className="text-lg font-semibold text-white">Recommended Next Steps</h3>
                </div>
                <ol className="space-y-2">
                  {aiAnalysis.nextSteps.map((step, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-cyber-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-sm font-medium text-cyber-400">{index + 1}</span>
                      </div>
                      <span className="text-cyber-200/90">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}


          </motion.div>
        </AnimatePresence>
      )}

      {/* Detailed Recommendation Modal */}
      <AnimatePresence>
        {showRecommendationModal && selectedRecommendation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeRecommendationModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900/95 backdrop-blur-sm border border-white/10 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-slate-900/95 backdrop-blur-sm border-b border-white/10 px-6 py-4 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      selectedRecommendation.priority === 'critical' ? 'bg-red-500/20' :
                      selectedRecommendation.priority === 'high' ? 'bg-orange-500/20' :
                      selectedRecommendation.priority === 'medium' ? 'bg-yellow-500/20' : 'bg-blue-500/20'
                    }`}>
                      <Shield className={`w-5 h-5 ${
                        selectedRecommendation.priority === 'critical' ? 'text-red-400' :
                        selectedRecommendation.priority === 'high' ? 'text-orange-400' :
                        selectedRecommendation.priority === 'medium' ? 'text-yellow-400' : 'text-blue-400'
                      }`} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">{selectedRecommendation.title}</h2>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                          selectedRecommendation.priority === 'critical' ? 'bg-red-500/20 text-red-400' :
                          selectedRecommendation.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                          selectedRecommendation.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {selectedRecommendation.priority?.toUpperCase()} PRIORITY
                        </span>
                        <span className="text-sm text-cyber-300/70">•</span>
                        <span className="text-sm text-cyber-200/90 capitalize">{selectedRecommendation.category}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={closeRecommendationModal}
                    className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  >
                    <X className="w-4 h-4 text-cyber-300" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Overview */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                    <Info className="w-5 h-5 mr-2 text-cyber-400" />
                    Overview
                  </h3>
                  <p className="text-cyber-200/90 leading-relaxed">{selectedRecommendation.description}</p>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-cyber-500/10 border border-cyber-500/20 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Gauge className="w-5 h-5 text-cyber-400" />
                      <span className="font-medium text-white">Impact Level</span>
                    </div>
                    <div className="text-xl font-bold text-cyber-300">{selectedRecommendation.impact}</div>
                    <div className="text-sm text-cyber-300/80">Expected security improvement</div>
                  </div>
                  
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Calendar className="w-5 h-5 text-yellow-400" />
                      <span className="font-medium text-white">Implementation Effort</span>
                    </div>
                    <div className="text-xl font-bold text-yellow-300">{selectedRecommendation.effort}</div>
                    <div className="text-sm text-yellow-300/80">Time and resources required</div>
                  </div>
                  
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Target className="w-5 h-5 text-green-400" />
                      <span className="font-medium text-white">Risk Reduction</span>
                    </div>
                    <div className="text-xl font-bold text-green-300">
                      {selectedRecommendation.priority === 'high' ? '85%' : 
                       selectedRecommendation.priority === 'medium' ? '65%' : '45%'}
                    </div>
                    <div className="text-sm text-green-300/80">Estimated risk mitigation</div>
                  </div>
                </div>

                {/* Implementation Details */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-cyber-400" />
                    Implementation Steps
                  </h3>
                  <div className="space-y-3">
                    {(selectedRecommendation.implementationSteps || [
                      `Assess current ${selectedRecommendation.category?.toLowerCase() || 'security'} infrastructure and identify gaps`,
                      `Develop a comprehensive ${selectedRecommendation.title?.toLowerCase() || 'security improvement'} strategy`,
                      `Allocate necessary resources and assign responsible team members`,
                      `Implement the solution in phases with proper testing`,
                      `Monitor effectiveness and adjust as needed`,
                      `Document processes and train relevant personnel`
                    ]).map((step, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-cyber-500/20 border border-cyber-500/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-sm font-medium text-cyber-300">{index + 1}</span>
                        </div>
                        <span className="text-cyber-200/90">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Business Impact */}
                <div className="bg-cyber-500/10 border border-cyber-500/20 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-cyber-400" />
                    Business Impact
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-cyber-200 mb-2">Benefits</h4>
                      <ul className="space-y-1 text-sm text-cyber-200/80">
                        {selectedRecommendation.businessImpact && (
                          <li>• {selectedRecommendation.businessImpact}</li>
                        )}
                        {(selectedRecommendation.successMetrics || [
                          'Enhanced security posture and compliance',
                          'Reduced risk of security incidents',
                          'Improved stakeholder confidence',
                          'Better regulatory compliance'
                        ]).map((benefit, index) => (
                          <li key={index}>• {benefit}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-cyber-200 mb-2">Considerations</h4>
                      <ul className="space-y-1 text-sm text-cyber-200/80">
                        {selectedRecommendation.estimatedCost && (
                          <li>• Estimated cost: {selectedRecommendation.estimatedCost}</li>
                        )}
                        {(selectedRecommendation.dependencies || [
                          'Initial implementation costs',
                          'Staff training requirements',
                          'Potential workflow adjustments',
                          'Ongoing maintenance needs'
                        ]).map((consideration, index) => (
                          <li key={index}>• {consideration}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Timeline & Resources */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <Calendar className="w-5 h-5 mr-2 text-cyber-400" />
                      Recommended Timeline
                    </h3>
                    <div className="space-y-2">
                      {selectedRecommendation.timeline ? (
                        <div className="flex justify-between items-center py-2 border-b border-cyber-500/20">
                          <span className="text-cyber-200/80">Overall Timeline</span>
                          <span className="font-medium text-cyber-200">{selectedRecommendation.timeline}</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between items-center py-2 border-b border-cyber-500/20">
                            <span className="text-cyber-200/80">Planning Phase</span>
                            <span className="font-medium text-cyber-200">1-2 weeks</span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-cyber-500/20">
                            <span className="text-cyber-200/80">Implementation</span>
                            <span className="font-medium text-cyber-200">
                              {(selectedRecommendation.implementationEffort || selectedRecommendation.effort) === 'High' || (selectedRecommendation.implementationEffort || selectedRecommendation.effort) === 'Weeks' ? '4-8 weeks' :
                               (selectedRecommendation.implementationEffort || selectedRecommendation.effort) === 'Medium' ? '2-4 weeks' : '1-2 weeks'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-2">
                            <span className="text-cyber-200/80">Testing & Validation</span>
                            <span className="font-medium text-cyber-200">1 week</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <Users className="w-5 h-5 mr-2 text-cyber-400" />
                      Required Resources
                    </h3>
                    <div className="space-y-2">
                      {(selectedRecommendation.resources || [
                        'Security team lead',
                        'IT infrastructure team',
                        'Compliance officer',
                        'External consultants (if needed)'
                      ]).map((resource, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-cyber-400 rounded-full"></div>
                          <span className="text-cyber-200/90">{resource}</span>
                        </div>
                      ))}
                      {selectedRecommendation.framework && (
                        <div className="mt-3 pt-2 border-t border-cyber-500/20">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                            <span className="text-cyber-200/90 text-sm">Framework: {selectedRecommendation.framework}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-gray-800/90 border-t border-cyber-500/20 px-6 py-4 rounded-b-xl">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-cyber-200/80">
                    <span className="font-medium">AI Confidence:</span> 
                    <span className="ml-1">{Math.round((aiAnalysis?.confidence || 0.85) * 100)}%</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={closeRecommendationModal}
                      className="px-4 py-2 text-cyber-200/80 hover:text-white transition-colors"
                    >
                      Close
                    </button>
                    <button className="btn-primary flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4" />
                      <span>Mark as Planned</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default AISummary